import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Dices, Star, ArrowLeft, RefreshCw } from 'lucide-react';
import Layout from '../components/Layout';
import api from '../lib/api';

/**
 * "I consigli di oggi" — 5 movies + 5 TV shows, deterministic per-day.
 * Shows a ~1 second dice animation ("Sto scegliendo i migliori titoli per te…")
 * before revealing the picks.
 */
export default function ConsigliOggi() {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [showList, setShowList] = useState(false); // reveal after dice animation
  const navigate = useNavigate();

  useEffect(() => {
    let cancel = false;
    setError('');
    // Kick off the fetch in parallel with the intro animation
    const fetchP = api.get('/tmdb/daily-picks').then(({ data }) => data).catch((e) => {
      setError(e?.response?.data?.detail || 'Impossibile caricare i consigli');
      return null;
    });
    const minDelay = new Promise((res) => setTimeout(res, 1200));
    Promise.all([fetchP, minDelay]).then(([d]) => {
      if (cancel) return;
      if (d) setData(d);
      setShowList(true);
    });
    return () => { cancel = true; };
  }, []);

  return (
    <Layout title="I consigli di oggi">
      <div className="min-h-[calc(100vh-64px)] px-5 pt-6 pb-24 max-w-3xl mx-auto">
        <button
          onClick={() => navigate('/home')}
          className="text-white/60 hover:text-[var(--gold)] text-xs flex items-center gap-1 mb-4 transition-colors"
          data-testid="consigli-back"
        >
          <ArrowLeft className="w-3.5 h-3.5" /> Home
        </button>

        <div className="mb-6">
          <div className="text-[10px] uppercase tracking-[0.25em] text-white/40 flex items-center gap-1.5">
            <RefreshCw className="w-2.5 h-2.5" />
            Aggiornato automaticamente ogni giorno da TMDB
          </div>
          <h1 className="font-heading text-3xl font-light mt-1 flex items-center gap-2">
            <span>🍿</span>I consigli di oggi
          </h1>
          {data?.date && (
            <div className="text-[11px] text-white/40 mt-1">{formatDay(data.date)}</div>
          )}
        </div>

        <AnimatePresence mode="wait">
          {!showList && (
            <motion.div
              key="intro"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="flex flex-col items-center justify-center py-24 gap-4"
              data-testid="consigli-loading"
            >
              <motion.div
                animate={{ rotate: [0, 360, 720, 1080] }}
                transition={{ duration: 1.2, ease: 'easeInOut' }}
                className="text-6xl"
              >🎲</motion.div>
              <div className="text-sm text-white/70 font-heading tracking-wide">
                Sto scegliendo i migliori titoli per te…
              </div>
              <div className="flex gap-1">
                {[0, 1, 2].map((i) => (
                  <motion.span
                    key={i}
                    className="w-1.5 h-1.5 rounded-full bg-[var(--gold)]"
                    animate={{ opacity: [0.2, 1, 0.2] }}
                    transition={{ duration: 1, repeat: Infinity, delay: i * 0.2 }}
                  />
                ))}
              </div>
            </motion.div>
          )}

          {showList && error && (
            <motion.div
              key="error"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-center py-16 text-white/60 text-sm"
            >
              {error}
            </motion.div>
          )}

          {showList && !error && data && (
            <motion.div
              key="list"
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="space-y-8"
              data-testid="consigli-list"
            >
              <Section title="🎬 8 Film consigliati" items={data.movies} media="movie" />
              <Section title="📺 8 Serie TV consigliate" items={data.tv} media="tv" />
              <div className="text-center pt-4">
                <div className="text-[10px] text-white/30">Torna domani per nuovi consigli 🌙</div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </Layout>
  );
}

function Section({ title, items, media }) {
  return (
    <div>
      <h2 className="font-heading text-lg mb-3">{title}</h2>
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
        {(items || []).map((it, i) => (
          <motion.div
            key={`${media}-${it.id}`}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.05 * i, duration: 0.35 }}
          >
            <Link
              to={`/tmdb/${media}/${it.id}`}
              data-testid={`consigli-card-${media}-${it.id}`}
              className="block group"
            >
              <div className="relative aspect-[2/3] w-full rounded-xl overflow-hidden border border-white/10 group-hover:border-[var(--gold)]/60 transition-all shadow-lg shadow-black/40">
                {it.poster ? (
                  <img
                    src={it.poster}
                    alt={it.title}
                    loading="lazy"
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105 active:scale-95"
                  />
                ) : (
                  <div className="w-full h-full bg-white/5" />
                )}
                <div className="absolute top-1.5 left-1.5 bg-black/70 backdrop-blur-sm rounded-md px-1.5 py-0.5 text-[10px] flex items-center gap-0.5">
                  <Star className="w-2.5 h-2.5 text-[var(--gold)] fill-[var(--gold)]" />
                  <span className="tabular-nums text-white/90">{it.vote?.toFixed ? it.vote.toFixed(1) : it.vote}</span>
                </div>
                {it.primary_genre && (
                  <div className="absolute bottom-1.5 left-1.5 right-1.5 text-[9px] uppercase tracking-widest text-white/90 bg-black/60 backdrop-blur-sm rounded px-1.5 py-0.5 text-center truncate">
                    {it.primary_genre}
                  </div>
                )}
              </div>
              <div className="mt-2 px-0.5">
                <div className="text-xs font-medium leading-tight line-clamp-2 group-hover:text-[var(--gold)] transition-colors">{it.title}</div>
                <div className="text-[10px] text-white/50 mt-0.5 tabular-nums">{it.year || '—'}</div>
              </div>
            </Link>
          </motion.div>
        ))}
      </div>
    </div>
  );
}

function formatDay(iso) {
  try {
    const d = new Date(iso + 'T12:00:00');
    return d.toLocaleDateString('it-IT', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' });
  } catch { return iso; }
}
